import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const TIERS = [
  {
    n: 1,
    label: 'All Staff',
    color: '#10b981',
    bg: 'rgba(16,185,129,0.08)',
    desc: 'Basic benefits available to every employee. Includes the standard offers from food, transport, and retail partners.',
  },
  {
    n: 2,
    label: 'Manager',
    color: '#00bf63',
    bg: 'rgba(0,191,99,0.08)',
    desc: 'Enhanced benefits for team leads and managers. Adds health, entertainment, and travel perks on top of Tier 1.',
  },
  {
    n: 3,
    label: 'Executive',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.08)',
    desc: 'Full access to every exclusive offer. Premium partners, higher discounts, priority redemptions.',
  },
];

const CompanyTiers = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ 1: 0, 2: 0, 3: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const stored = localStorage.getItem('companyData');
      if (!stored) {
        navigate('/company/login');
        return;
      }
      const company = JSON.parse(stored);
      const snap = await getDocs(query(collection(db, 'users'), where('companyId', '==', company.id)));
      const tally = { 1: 0, 2: 0, 3: 0 };
      snap.docs.forEach((d) => {
        const t = Number(d.data().tier) || 1;
        if (tally[t] !== undefined) tally[t]++;
      });
      setCounts(tally);
    } catch (err) {
      console.error('Error loading tier counts:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>Benefit Tiers</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
          How perks scale across your team
        </div>
        <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, margin: '6px 0 0 0', maxWidth: 640 }}>
          Loysta uses a 3-tier system. Assign tiers to employees on the Employees page — each tier unlocks more partners and bigger discounts.
        </p>
      </div>

      {TIERS.map((t) => (
        <div
          key={t.n}
          className="loysta-card"
          style={{
            padding: '24px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div style={{
            width: 60, height: 60, borderRadius: 16,
            background: t.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            border: `1px solid ${t.color}33`,
          }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: t.color }}>T{t.n}</span>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                Tier {t.n} — {t.label}
              </span>
              <span style={{
                padding: '2px 10px',
                borderRadius: 999,
                background: t.bg,
                color: t.color,
                fontSize: 12,
                fontWeight: 600,
              }}>
                {loading ? '—' : `${counts[t.n] || 0} ${counts[t.n] === 1 ? 'employee' : 'employees'}`}
              </span>
            </div>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: 0 }}>{t.desc}</p>
          </div>

          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => navigate('/company/employees')}
              style={{
                padding: '9px 18px',
                border: '1.5px solid #e2e8f0',
                borderRadius: 8,
                background: 'white',
                color: '#334155',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00bf63'; e.currentTarget.style.color = '#00bf63'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
            >Manage employees</button>
          </div>
        </div>
      ))}

      <div className="loysta-card" style={{ padding: '20px 22px', borderLeft: '3px solid #00bf63', borderRadius: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
          Customising tiers
        </div>
        <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65, margin: 0 }}>
          Tier definitions are managed centrally by Loysta — they're the same across every company so vendors can target perks consistently. Reach out to your Loysta account manager if you'd like to discuss custom tiers for your organization.
        </p>
      </div>
    </div>
  );
};

export default CompanyTiers;
